import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSite } from '../../context/SiteContext';
import { Line, Bar, Chart as ChartComponent } from 'react-chartjs-2';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

// Registrar plugin treemap de forma estática
ChartJS.register(TreemapController, TreemapElement);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { getCurrentSiteId, currentSite, loading: siteLoading } = useSite();
  const siteId = currentSite?.id || getCurrentSiteId();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ total_quantity: 0, total_records: 0, unique_products: 0, unique_lines: 0 });
  const [trend, setTrend] = useState([]);
  const [pareto, setPareto] = useState([]);
  const [kpis, setKpis] = useState({ produced: null, defects: null, fpy_percent: null, sigma_level: null, cpk: null });

  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    lines: [],
    shifts: [],
    product_contains: '',
    defect_contains: ''
  });
  const [showLines, setShowLines] = useState(false);
  const [showShifts, setShowShifts] = useState(false);
  const linesDropdownRef = useRef(null);
  const shiftsDropdownRef = useRef(null);

  const [catalogs, setCatalogs] = useState({ lines: [], shifts: [] });
  const [topN, setTopN] = useState(20);
  const filtersRef = useRef(filters);
  const topNRef = useRef(topN);
  filtersRef.current = filters;
  topNRef.current = topN;

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const activeFilters = filtersRef.current;
      const params = { site_id: siteId, top: topNRef.current };

      if (activeFilters.date_from) params.date_from = activeFilters.date_from;
      if (activeFilters.date_to) params.date_to = activeFilters.date_to;
      if (activeFilters.lines?.length) params.lines = activeFilters.lines.join(',');
      if (activeFilters.shifts?.length) params.shifts = activeFilters.shifts.join(',');
      if (activeFilters.product_contains) params.product_contains = activeFilters.product_contains;
      if (activeFilters.defect_contains) params.defect_contains = activeFilters.defect_contains;

      const { data } = await axios.get('/analytics/dashboard', { headers, params });

      setSummary(data.summary);
      setTrend(data.trend);
      setPareto(data.pareto);
      setByLineType(data.by_line_type);
      setMatrixDateShift(data.matrix_date_shift);
      setTreemap(data.treemap);
      setKpis(data.kpis);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [navigate, siteId]);

  useEffect(() => {
    if (authLoading || siteLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!['analyst', 'admin', 'admin_master', 'admin_site', 'admin_analytics'].includes(user.role)) {
      setError(t('dashboard.permissions.noAccess'));
      setLoading(false);
      return;
    }
    fetchAll();
  }, [authLoading, fetchAll, navigate, siteLoading, t, user]);

  // Load catalogs for filters
  useEffect(() => {
    const loadCats = async () => {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = siteId ? { site_id: siteId } : {};
      
      try {
        const { data } = await axios.get('/catalogs/all', { headers, params });
        setCatalogs({ lines: data.lines || [], shifts: data.shifts || [] });
      } catch (e) {}
    };
    if (!authLoading && !siteLoading && user) loadCats();
  }, [authLoading, siteId, siteLoading, user]);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Dropdown listbox handlers
  const toggleLine = (code) => {
    setFilters((prev) => ({
      ...prev,
      lines: prev.lines.includes(code)
        ? prev.lines.filter((l) => l !== code)
        : [...prev.lines, code],
    }));
  };
  const toggleShift = (code) => {
    setFilters((prev) => ({
      ...prev,
      shifts: prev.shifts.includes(code)
        ? prev.shifts.filter((s) => s !== code)
        : [...prev.shifts, code],
    }));
  };
  // Cierra dropdowns al hacer click fuera
  useEffect(() => {
    function handleClick(e) {
      if (showLines && linesDropdownRef.current && !linesDropdownRef.current.contains(e.target)) setShowLines(false);
      if (showShifts && shiftsDropdownRef.current && !shiftsDropdownRef.current.contains(e.target)) setShowShifts(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showLines, showShifts]);

  const applyFilters = (e) => {
    e.preventDefault();
    fetchAll();
  };

  const trendLabels = trend.map((t) => t.date);
  // Sanitizar valores: asegurar número entero positivo razonable
  const rawTrendValues = trend.map((t) => {
    const v = Number(t.total_quantity);
    if (!isFinite(v) || v < 0) return 0;
    return Math.round(v);
  });
  // Detectar valores claramente fuera de rango (ej. > 1e6) y normalizar (posible dato corrupto)
  const MAX_REASONABLE = 1_000_000; // ajustar si se requieren números mayores
  const hasOutlier = rawTrendValues.some(v => v > MAX_REASONABLE);
  const trendValues = hasOutlier ? rawTrendValues.map(v => (v > MAX_REASONABLE ? MAX_REASONABLE : v)) : rawTrendValues;
  
  // --- Proyección a 3 días (regresión lineal simple) ---
  const projectionDays = 3;
  let projectionLabels = [];
  let projectionValues = [];
  if (trendValues.length >= 2) {
    const n = trendValues.length;
    const xs = trendValues.map((_, i) => i + 1); // 1..n
    const ys = trendValues;
    const sumX = xs.reduce((s,v)=>s+v,0);
    const sumY = ys.reduce((s,v)=>s+v,0);
    const sumXY = xs.reduce((s,v,i)=>s + v * ys[i],0);
    const sumXX = xs.reduce((s,v)=>s + v*v,0);
    const denom = (n * sumXX - sumX * sumX) || 1;
    const m = (n * sumXY - sumX * sumY) / denom; // pendiente
    const b = (sumY - m * sumX) / n; // intercepto
    // Última fecha base
    const lastDateStr = trendLabels[trendLabels.length - 1];
    const baseDate = new Date(lastDateStr + 'T00:00:00');
    for (let d = 1; d <= projectionDays; d++) {
      const futureX = n + d; // posición futura
      let pred = m * futureX + b;
      if (!isFinite(pred)) pred = ys[ys.length - 1];
      if (pred < 0) pred = 0; // no negativos
      projectionValues.push(Math.round(pred));
      const futureDate = new Date(baseDate);
      futureDate.setDate(baseDate.getDate() + d);
      projectionLabels.push(futureDate.toISOString().slice(0,10));
    }
  } else if (trendValues.length === 1) {
    // Serie de un solo punto: repetir valor
    const lastDateStr = trendLabels[0];
    const baseDate = new Date(lastDateStr + 'T00:00:00');
    for (let d = 1; d <= projectionDays; d++) {
      const futureDate = new Date(baseDate);
      futureDate.setDate(baseDate.getDate() + d);
      projectionLabels.push(futureDate.toISOString().slice(0,10));
      projectionValues.push(trendValues[0]);
    }
  }
  const extendedTrendLabels = [...trendLabels, ...projectionLabels];

  const projectionDataset = projectionValues.length ? [{
    label: t('dashboard.charts.projection'),
    data: [...new Array(trendValues.length - 1).fill(null), trendValues[trendValues.length - 1], ...projectionValues],
    borderColor: '#6f42c1',
    backgroundColor: 'rgba(111,66,193,0.15)',
    borderDash: [2,3],
    pointStyle: 'triangle',
    spanGaps: true,
    fill: false
  }] : [];

  const trendData = {
    labels: extendedTrendLabels,
    datasets: [
      {
        label: t('dashboard.charts.quantity'),
        data: trendValues,
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 110, 253, 0.2)'
      },
      ...projectionDataset
    ]
  };

  // (Se moverá más abajo después de declarar showPercentParetos)

  // New state for extended charts
  const [byLineType, setByLineType] = useState([]);
  const [matrixDateShift, setMatrixDateShift] = useState([]);
  const [treemap, setTreemap] = useState([]);
  const [treemapMode, setTreemapMode] = useState('chart'); // default gráfico
  const treemapChartRef = useRef(null);
  const [showPercentParetos, setShowPercentParetos] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  // Nuevo: selección de línea para Pareto de defectos por línea específica
  const [selectedLineForDefectPareto, setSelectedLineForDefectPareto] = useState('');
  // Paginación para Treemap
  const [treemapPage, setTreemapPage] = useState(1);
  const TREEMAP_PAGE_SIZE = 20;

  useEffect(() => {
    // Reiniciar a la página 1 cuando cambian los datos del treemap
    setTreemapPage(1);
  }, [treemap]);

  // Pivot robusto: línea -> tipo -> cantidad (numérica)
  const numericBLT = (byLineType || []).map(r => ({
    ...r,
    total_quantity: Number(r.total_quantity) || 0,
  }));
  const allLines = Array.from(new Set(numericBLT.map((x) => x.line_code)));
  const allTypes = Array.from(new Set(numericBLT.map((x) => x.defect_type)));

  const pivot = allLines.reduce((acc, line) => {
    acc[line] = allTypes.reduce((m, t) => { m[t] = 0; return m; }, {});
    return acc;
  }, {});
  numericBLT.forEach(r => {
    if (pivot[r.line_code] && Object.prototype.hasOwnProperty.call(pivot[r.line_code], r.defect_type)) {
      pivot[r.line_code][r.defect_type] += r.total_quantity;
    }
  });

  // Si no hay selección todavía y hay líneas, seleccionar la primera
  if (!selectedLineForDefectPareto && allLines.length) {
    // Nota: esto ejecutará cada render inicial hasta que se establezca el estado, pero React consolidará.
    setTimeout(()=> setSelectedLineForDefectPareto(allLines[0]), 0);
  }

  const readableType = (t) => (catalogs.defect_types || []).find(dt => (dt.code || dt.name) === t)?.name || t;

  const stackedData = {
    labels: allLines,
    datasets: allTypes.map((t, idx) => ({
      label: readableType(t),
      data: allLines.map((l) => pivot[l][t] || 0),
      backgroundColor: `hsl(${(idx * 55) % 360} 70% 60% / 0.6)`
    }))
  };

  const mixData = {
    labels: allLines,
    datasets: allTypes.map((t, idx) => {
      return {
        label: readableType(t),
        data: allLines.map((l) => {
          const total = allTypes.reduce((s, tt) => s + (pivot[l][tt] || 0), 0);
          const val = pivot[l][t] || 0;
          return total > 0 ? Math.round((val / total) * 100) : 0;
        }),
        backgroundColor: `hsl(${(idx * 55) % 360} 70% 50% / 0.6)`
      };
    })
  };

  // Preparar datos para Treemap Chart (producto->tipo)
  const treemapTree = treemap.map(r => ({
    product: r.product_name || r.product_code,
    product_code: r.product_code,
    defect_type: r.defect_type,
    value: Number(r.total_quantity) || 0
  }));

  const colorForType = (type) => {
    const idx = [...new Set(treemap.map(x => x.defect_type))].indexOf(type);
    return `hsl(${(idx * 53) % 360} 65% 55% / 0.75)`;
  };

  const treemapChartData = {
    datasets: [
      {
        tree: treemapTree,
        key: 'value',
        groups: ['product', 'defect_type'],
        spacing: 1,
        borderWidth: 1,
        borderColor: '#ffffff',
        backgroundColor: (ctx) => {
          const item = ctx.raw?._data;
            if (!item) return 'rgba(0,0,0,0.1)';
            return colorForType(item.defect_type);
        },
        labels: {
          display: true,
          formatter: (ctx) => {
            const v = ctx.raw.v;
            const d = ctx.raw?._data;
            if (!d) return '';
            // Mostrar producto (abreviado) y tipo con valor
            const prod = (d.product || '').substring(0, 10);
            return `${prod}\n${d.defect_type}\n${v}`;
          },
          color: '#111',
          font: { size: 10 }
        }
      }
    ]
  };

  const treemapOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          title: (items) => {
            const d = items[0].raw?._data;
            return d ? `${d.product} (${d.product_code})` : '';
          },
          label: (item) => {
            const d = item.raw?._data;
            if (!d) return '';
            return `${d.defect_type}: ${item.raw.v}`;
          }
        }
      },
      legend: { display: false }
    }
  };

  // ---- PARETOS ADICIONALES ----
  // Pareto por Línea (sumatoria de tipos por línea)
  const paretoLine = allLines.map(line => {
    const total = allTypes.reduce((s, t) => s + (pivot[line][t] || 0), 0);
    return { line, total };
  }).filter(r => r.total > 0).sort((a,b)=>b.total-a.total);
  const grandLine = paretoLine.reduce((s,r)=>s+r.total,0) || 1;
  const paretoLineCumPct = (()=>{let run=0; return paretoLine.map(r=>{run+=r.total; return +(run/grandLine*100).toFixed(2);});})();
  const paretoLineData = {
    labels: paretoLine.map(r=>r.line),
    datasets: [
      {
        type: 'bar',
        label: showPercentParetos ? t('dashboard.charts.percentOfTotal') : t('dashboard.charts.quantity'),
        data: showPercentParetos ? paretoLine.map(r=> +(r.total/grandLine*100).toFixed(2)) : paretoLine.map(r=>r.total),
        backgroundColor: 'rgba(30,136,229,0.65)',
        yAxisID: showPercentParetos ? 'y1':'y'
      },
      {
        type: 'line',
        label: t('dashboard.charts.cumulative'),
        data: paretoLineCumPct,
        borderColor: '#ff6f00',
        backgroundColor: '#ff6f00',
        yAxisID: 'y1',
        tension: 0.25,
        pointRadius: 3,
        fill: false
      }
    ]
  };

  // Pareto por Turno (usar matrixDateShift)
  const shiftTotalsMap = {};
  (matrixDateShift||[]).forEach(r => {
    shiftTotalsMap[r.shift_code] = (shiftTotalsMap[r.shift_code]||0) + (Number(r.total_quantity)||0);
  });
  const paretoShiftArr = Object.entries(shiftTotalsMap).map(([shift, total])=>({shift, total})).filter(r=>r.total>0).sort((a,b)=>b.total-a.total);
  const grandShift = paretoShiftArr.reduce((s,r)=>s+r.total,0)||1;
  const paretoShiftCumPct = (()=>{let run=0; return paretoShiftArr.map(r=>{run+=r.total; return +(run/grandShift*100).toFixed(2);});})();
  const paretoShiftData = {
    labels: paretoShiftArr.map(r=>r.shift),
    datasets: [
      {
        type:'bar',
        label: showPercentParetos ? t('dashboard.charts.percentOfTotal') : t('dashboard.charts.quantity'),
        data: showPercentParetos ? paretoShiftArr.map(r=> +(r.total/grandShift*100).toFixed(2)) : paretoShiftArr.map(r=>r.total),
        backgroundColor: 'rgba(30,136,229,0.65)',
        yAxisID: showPercentParetos ? 'y1':'y'
      },
      {
        type:'line',
        label: t('dashboard.charts.cumulative'),
        data: paretoShiftCumPct,
        borderColor: '#ff6f00',
        backgroundColor: '#ff6f00',
        yAxisID: 'y1',
        tension: 0.25,
        pointRadius: 3,
        fill: false
      }
    ]
  };

  // Pareto por Producto (usar treemap agregado por producto)
  const productTotalsMap = {};
  (treemap||[]).forEach(r => {
    const key = r.product_code;
    productTotalsMap[key] = (productTotalsMap[key]||0) + (Number(r.total_quantity)||0);
  });
  const fullParetoProductArr = Object.entries(productTotalsMap).map(([product_code, total])=>({product_code, total})).filter(r=>r.total>0).sort((a,b)=>b.total-a.total);
  const paretoProductArr = showAllProducts ? fullParetoProductArr : fullParetoProductArr.slice(0,30);
  const grandProduct = fullParetoProductArr.reduce((s,r)=>s+r.total,0)||1;
  const paretoProductCumPct = (()=>{let run=0; return paretoProductArr.map(r=>{run+=r.total; return +(run/grandProduct*100).toFixed(2);});})();
  const paretoProductData = {
    labels: paretoProductArr.map(r=>r.product_code),
    datasets: [
      {
        type:'bar',
        label: showPercentParetos ? t('dashboard.charts.percentOfTotal') : t('dashboard.charts.quantity'),
        data: showPercentParetos ? paretoProductArr.map(r=> +(r.total/grandProduct*100).toFixed(2)) : paretoProductArr.map(r=>r.total),
        backgroundColor: 'rgba(30,136,229,0.65)',
        yAxisID: showPercentParetos ? 'y1':'y'
      },
      {
        type:'line',
        label: t('dashboard.charts.cumulative'),
        data: paretoProductCumPct,
        borderColor: '#ff6f00',
        backgroundColor: '#ff6f00',
        yAxisID: 'y1',
        tension: 0.25,
        pointRadius: 3,
        fill: false
      }
    ]
  };

  const sharedParetoOptions = {
    responsive:true,
    maintainAspectRatio:false,
    interaction:{mode:'index', intersect:false},
    stacked:false,
    scales:{
      y:{
        beginAtZero:true,
        title:{display: !showPercentParetos, text: showPercentParetos ? '' : t('dashboard.charts.quantity')},
      },
      y1:{
        beginAtZero:true,
        position:'right',
        grid:{drawOnChartArea:false},
        max:100,
        ticks:{callback:(v)=> v+'%'},
        title:{display:true, text: t('dashboard.charts.cumulative')}
      }
    },
    plugins:{
      tooltip:{
        callbacks:{
          label:(ctx)=>{
            if(ctx.dataset.type==='line') return `Acum: ${ctx.formattedValue}%`;
            if(showPercentParetos) return `%: ${ctx.formattedValue}%`;
            return `${ctx.dataset.label}: ${ctx.formattedValue}`;
          }
        }
      }
    }
  };

  // Pareto por Tipo enriquecido (ya con showPercentParetos definido)
  const paretoTypeArr = pareto.map(p => ({ defect_type: p.defect_type, total: Number(p.total_quantity)||0 }))
    .filter(r=>r.total>0)
    .sort((a,b)=>b.total-a.total);
  const grandType = paretoTypeArr.reduce((s,r)=>s+r.total,0)||1;
  const paretoTypeCumPct = (()=>{let run=0; return paretoTypeArr.map(r=>{run+=r.total; return +(run/grandType*100).toFixed(2);});})();
  const paretoTypeData = {
    labels: paretoTypeArr.map(r=>r.defect_type),
    datasets: [
      {
        type:'bar',
        label: showPercentParetos ? t('dashboard.charts.percentOfTotal') : t('dashboard.charts.quantity'),
        data: showPercentParetos ? paretoTypeArr.map(r=> +(r.total/grandType*100).toFixed(2)) : paretoTypeArr.map(r=>r.total),
        backgroundColor: 'rgba(30,136,229,0.65)',
        yAxisID: showPercentParetos ? 'y1':'y'
      },
      {
        type:'line',
        label: t('dashboard.charts.cumulative'),
        data: paretoTypeCumPct,
        borderColor: '#ff6f00',
        backgroundColor: '#ff6f00',
        yAxisID: 'y1',
        tension: 0.25,
        pointRadius: 3,
        fill: false
      }
    ]
  };

  // ---- Pareto por Defecto dentro de Línea seleccionada ----
  let lineDefectParetoArr = [];
  if (selectedLineForDefectPareto) {
    const map = pivot[selectedLineForDefectPareto] || {};
    lineDefectParetoArr = Object.entries(map)
      .map(([defect_type, total]) => ({ defect_type, total }))
      .filter(r => r.total > 0)
      .sort((a,b)=> b.total - a.total);
  }
  const grandLineDefect = lineDefectParetoArr.reduce((s,r)=>s+r.total,0) || 1;
  const lineDefectCumPct = (()=>{let run=0; return lineDefectParetoArr.map(r=>{run+=r.total; return +(run/grandLineDefect*100).toFixed(2);});})();
  const lineDefectParetoData = {
    labels: lineDefectParetoArr.map(r=>r.defect_type),
    datasets: [
      {
        type:'bar',
        label: showPercentParetos ? t('dashboard.charts.percentOfTotal') : t('dashboard.charts.quantity'),
        data: showPercentParetos ? lineDefectParetoArr.map(r=> +(r.total/grandLineDefect*100).toFixed(2)) : lineDefectParetoArr.map(r=>r.total),
        backgroundColor: 'rgba(30,136,229,0.65)',
        yAxisID: showPercentParetos ? 'y1':'y'
      },
      {
        type:'line',
        label: t('dashboard.charts.cumulative'),
        data: lineDefectCumPct,
        borderColor: '#ff6f00',
        backgroundColor: '#ff6f00',
        yAxisID: 'y1',
        tension: 0.25,
        pointRadius: 3,
        fill: false
      }
    ]
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('dashboard.title')}</h1>
      <p className="text-gray-700 mb-6">{t('dashboard.subtitle')}</p>

      <form className="flex flex-wrap gap-4 mb-6" onSubmit={applyFilters}>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-800 mb-1">{t('dashboard.filters.dateFrom')}</label>
          <input name="date_from" type="date" className="rounded border border-gray-300 px-2 py-1 text-gray-900" value={filters.date_from} onChange={onFilterChange} />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-800 mb-1">{t('dashboard.filters.dateTo')}</label>
          <input name="date_to" type="date" className="rounded border border-gray-300 px-2 py-1 text-gray-900" value={filters.date_to} onChange={onFilterChange} />
        </div>
        {/* Dropdown Listbox para Líneas */}
        <div className="flex flex-col relative" ref={linesDropdownRef}>
          <label className="text-sm font-semibold text-gray-800 mb-1">{t('dashboard.filters.lines')}</label>
          <button type="button" className="rounded border border-gray-300 px-2 py-1 text-gray-900 min-w-[120px] text-left bg-white hover:bg-gray-50" onClick={() => setShowLines((v) => !v)}>
            {filters.lines.length === 0 ? t('common.select')+'...' : `${filters.lines.length} ${t('common.select')}...`}
            <span className="float-right ml-2">▼</span>
          </button>
          {showLines && (
            <div className="absolute z-20 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-auto">
              {catalogs.lines.map((l) => (
                <label key={l.id} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.lines.includes(l.code)}
                    onChange={() => toggleLine(l.code)}
                    className="mr-2 accent-blue-600"
                  />
                  <span className="text-gray-800">{l.name} ({l.code})</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {/* Dropdown Listbox para Turnos */}
        <div className="flex flex-col relative" ref={shiftsDropdownRef}>
          <label className="text-sm font-semibold text-gray-800 mb-1">{t('dashboard.filters.shifts')}</label>
          <button type="button" className="rounded border border-gray-300 px-2 py-1 text-gray-900 min-w-[120px] text-left bg-white hover:bg-gray-50" onClick={() => setShowShifts((v) => !v)}>
            {filters.shifts.length === 0 ? t('common.select')+'...' : `${filters.shifts.length} ${t('common.select')}...`}
            <span className="float-right ml-2">▼</span>
          </button>
          {showShifts && (
            <div className="absolute z-20 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-auto">
              {catalogs.shifts.map((s) => (
                <label key={s.id} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.shifts.includes(s.code)}
                    onChange={() => toggleShift(s.code)}
                    className="mr-2 accent-blue-600"
                  />
                  <span className="text-gray-800">{s.name} ({s.code})</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-800 mb-1">{t('dashboard.filters.productContains')}</label>
          <input name="product_contains" type="text" className="rounded border border-gray-300 px-2 py-1 text-gray-900" value={filters.product_contains} onChange={onFilterChange} placeholder="p.ej. 123" />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-800 mb-1">{t('dashboard.filters.defectContains')}</label>
          <input name="defect_contains" type="text" className="rounded border border-gray-300 px-2 py-1 text-gray-900" value={filters.defect_contains} onChange={onFilterChange} placeholder="p.ej. Dimen" />
        </div>
        <div className="flex flex-col justify-end">
          <button className="bg-blue-600 text-white font-semibold rounded px-4 py-2 hover:bg-blue-700 transition disabled:opacity-60" type="submit" disabled={loading}>{t('common.apply')}</button>
        </div>
      </form>

      {error && (
        <div className="bg-red-100 text-red-800 px-4 py-2 rounded mb-4 font-semibold">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-5 text-lg text-gray-700">{t('common.loading')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-100 to-blue-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-blue-900 mb-2">{t('dashboard.kpis.totalDefects')}</span>
              <span className="text-4xl font-extrabold text-blue-700 drop-shadow">{summary.total_quantity}</span>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-green-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-green-900 mb-2">{t('dashboard.kpis.totalRecords')}</span>
              <span className="text-4xl font-extrabold text-green-700 drop-shadow">{summary.total_records}</span>
            </div>
            <div className="bg-gradient-to-br from-yellow-100 to-yellow-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-yellow-900 mb-2">{t('dashboard.kpis.uniqueProducts')}</span>
              <span className="text-4xl font-extrabold text-yellow-600 drop-shadow">{summary.unique_products}</span>
            </div>
            <div className="bg-gradient-to-br from-cyan-100 to-cyan-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-cyan-900 mb-2">{t('dashboard.kpis.uniqueLines')}</span>
              <span className="text-4xl font-extrabold text-cyan-700 drop-shadow">{summary.unique_lines}</span>
            </div>
          </div>

          {/* Segunda fila de KPIs: FPY, CPK, Sigma */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-emerald-100 to-emerald-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-emerald-900 mb-2">{t('dashboard.kpis.fpy')}</span>
              <span className="text-4xl font-extrabold text-emerald-700 drop-shadow">{(kpis?.fpy_percent ?? 0).toString()}%</span>
              <span className="text-xs text-emerald-900 mt-2">{t('dashboard.kpis.context', { produced: kpis.produced ?? 0, defects: kpis.defects ?? 0 })}</span>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-purple-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-purple-900 mb-2">{t('dashboard.kpis.cpk')}</span>
              <span className="text-4xl font-extrabold text-purple-700 drop-shadow">{kpis?.cpk ?? 0}</span>
            </div>
            <div className="bg-gradient-to-br from-rose-100 to-rose-300 rounded-2xl shadow-lg p-8 flex flex-col items-center min-h-[140px]">
              <span className="text-lg font-semibold text-rose-900 mb-2">{t('dashboard.kpis.sigma')}</span>
              <span className="text-4xl font-extrabold text-rose-700 drop-shadow">{kpis?.sigma_level ?? 0}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.charts.dailyTrend')}</h3>
              <div className="h-64 md:h-72 xl:h-80 2xl:h-96">
                {trend.length === 0 ? (
                  <p className="text-center text-gray-400">{t('common.noData')}</p>
                ) : (
                  <Line data={trendData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (val) => {
                            // Formateo legible: evitar notación científica
                            if (Math.abs(val) >= 1000) return Intl.NumberFormat('es-MX').format(val);
                            return val;
                          }
                        }
                      }
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const val = ctx.parsed.y;
                            if (val == null) return '';
                            const isProjection = projectionLabels.includes(ctx.label);
                            if (ctx.dataset.label.startsWith('Proyección')) {
                              return `Proyección: ${Intl.NumberFormat('es-MX').format(val)}`;
                            }
                            return `${isProjection ? 'Base' : t('dashboard.charts.quantity')}: ${Intl.NumberFormat('es-MX').format(val)}`;
                          }
                        }
                      }
                    }
                  }} />
                )}
              </div>
            </div>
            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.charts.paretoType')}</h3>
              <div className="h-64 md:h-72 xl:h-80 2xl:h-96">
                {paretoTypeArr.length === 0 ? (
                  <p className="text-center text-gray-400">{t('common.noData')}</p>
                ) : (
                  <Bar data={paretoTypeData} options={sharedParetoOptions} />
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.charts.stackedLineType')}</h3>
              <div className="h-72 xl:h-80 2xl:h-96">
                {byLineType.length === 0 ? (
                  <p className="text-center text-gray-400">{t('common.noData')}</p>
                ) : (
                  <Bar data={stackedData} options={{ responsive: true, maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true } } }} />
                )}
              </div>
            </div>
            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.charts.mix100')}</h3>
              <div className="h-72 xl:h-80 2xl:h-96">
                {byLineType.length === 0 ? (
                  <p className="text-center text-gray-400">{t('common.noData')}</p>
                ) : (
                  <Bar data={mixData} options={{ responsive: true, maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true, ticks: { callback: (v) => `${v}%` }, max: 100 } } }} />
                )}
              </div>
            </div>
          </div>

          {/* Paretos adicionales */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.charts.additionalParetos')}</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={()=>setShowPercentParetos(p=>!p)} className="px-3 py-1 rounded border text-sm font-medium bg-white hover:bg-gray-50 border-gray-300">
                {showPercentParetos ? t('dashboard.charts.viewQuantities') : t('dashboard.charts.viewPercent')}
              </button>
              <button type="button" onClick={()=>setShowAllProducts(p=>!p)} className="px-3 py-1 rounded border text-sm font-medium bg-white hover:bg-gray-50 border-gray-300">
                {showAllProducts ? t('dashboard.charts.top30') : t('dashboard.charts.viewAll')}
              </button>
            </div>
          </div>
          {/* Paretos en formato ancho completo */}
          <div className="space-y-6 mb-8">
            <div className="bg-white rounded shadow p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.charts.paretoLine')}</h3>
              </div>
              <div className="h-64 md:h-72">
                {paretoLine.length===0 ? <p className="text-center text-gray-400 mt-10">{t('common.noData')}</p> : <Bar data={paretoLineData} options={sharedParetoOptions} />}
              </div>
            </div>
            <div className="bg-white rounded shadow p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.charts.paretoShift')}</h3>
              </div>
              <div className="h-64 md:h-72">
                {paretoShiftArr.length===0 ? <p className="text-center text-gray-400 mt-10">{t('common.noData')}</p> : <Bar data={paretoShiftData} options={sharedParetoOptions} />}
              </div>
            </div>
            <div className="bg-white rounded shadow p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.charts.paretoProduct')} {showAllProducts ? `(${t('dashboard.charts.allProducts')})` : `(${t('dashboard.charts.top30')})`}</h3>
              </div>
              <div className="h-64 md:h-72">
                {paretoProductArr.length===0 ? <p className="text-center text-gray-400 mt-10">{t('common.noData')}</p> : <Bar data={paretoProductData} options={sharedParetoOptions} />}
              </div>
            </div>
            {/* Nuevo: Pareto de Defectos por Línea seleccionada */}
            <div className="bg-white rounded shadow p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.charts.paretoLineDefects')}</h3>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 font-medium" htmlFor="lineDefectParetoSelect">{t('dashboard.charts.line')}:</label>
                  <select
                    id="lineDefectParetoSelect"
                    value={selectedLineForDefectPareto}
                    onChange={e=> setSelectedLineForDefectPareto(e.target.value)}
                    className="px-2 py-1 rounded border border-gray-300 text-sm bg-white"
                  >
                    {allLines.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="h-64 md:h-72">
                {(!selectedLineForDefectPareto || lineDefectParetoArr.length===0) ? (
                  <p className="text-center text-gray-400 mt-10">{t('common.noData')}</p>
                ) : (
                  <Bar data={lineDefectParetoData} options={sharedParetoOptions} />
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded shadow p-4 mb-6">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.charts.treemap')} ({treemapMode === 'list' ? t('dashboard.charts.list') : t('dashboard.charts.chart')})</h3>
              <div className="flex gap-2 flex-wrap items-center">
                <div className="flex items-center gap-1 text-sm">
                  <label htmlFor="topN" className="font-medium text-gray-700">{t('dashboard.charts.topN')}:</label>
                  <input id="topN" type="number" min={5} max={200} step={5} value={topN} onChange={e=> setTopN(Number(e.target.value)||20)} className="w-20 px-2 py-1 rounded border border-gray-300" />
                  <button type="button" onClick={()=> fetchAll()} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-xs font-medium">{t('common.update')}</button>
                </div>
                <button
                  type="button"
                  onClick={() => setTreemapMode('list')}
                  className={`px-3 py-1 rounded text-sm font-medium border ${treemapMode==='list' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >{t('dashboard.charts.list').charAt(0).toUpperCase()+t('dashboard.charts.list').slice(1)}</button>
                <button
                  type="button"
                  onClick={() => setTreemapMode('chart')}
                  className={`px-3 py-1 rounded text-sm font-medium border ${treemapMode==='chart' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >{t('dashboard.charts.chart').charAt(0).toUpperCase()+t('dashboard.charts.chart').slice(1)}</button>
                <button type="button" onClick={()=>{
                  if(!treemapChartRef.current) return; 
                  const url = treemapChartRef.current.toBase64Image('image/png',1);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'treemap.png'; a.click();
                }} className="px-3 py-1 rounded text-sm font-medium border bg-white text-gray-700 border-gray-300 hover:bg-gray-50">{t('dashboard.charts.exportPNG')}</button>
              </div>
            </div>
            {treemap.length === 0 ? (
              <p className="text-center text-gray-400">{t('common.noData')}</p>
            ) : treemapMode === 'list' ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left text-gray-800">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-1 font-semibold">{t('dashboard.charts.product')}</th>
                      <th className="px-2 py-1 font-semibold">{t('dashboard.charts.type')}</th>
                      <th className="px-2 py-1 font-semibold">{t('dashboard.charts.quantity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {treemap
                      .slice((treemapPage - 1) * TREEMAP_PAGE_SIZE, treemapPage * TREEMAP_PAGE_SIZE)
                      .map((r, i) => (
                      <tr key={`${r.product_code}-${r.defect_type}-${i}`}>
                        <td className="px-2 py-1">{r.product_name} ({r.product_code})</td>
                        <td className="px-2 py-1">{r.defect_type}</td>
                        <td className="px-2 py-1">{r.total_quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-3">
                  {(() => {
                    const total = treemap.length;
                    const start = total === 0 ? 0 : (treemapPage - 1) * TREEMAP_PAGE_SIZE + 1;
                    const end = Math.min(treemapPage * TREEMAP_PAGE_SIZE, total);
                    const totalPages = Math.max(1, Math.ceil(total / TREEMAP_PAGE_SIZE));
                    return (
                      <>
                        <div className="text-gray-700 text-sm">Mostrando {start}–{end} de {total}</div>
                        <div className="flex items-center gap-2">
                          <button type="button" className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50" onClick={() => setTreemapPage((p) => Math.max(1, p - 1))} disabled={treemapPage <= 1}>◀ Anterior</button>
                          <span className="text-sm text-gray-700">Página {treemapPage} de {totalPages}</span>
                          <button type="button" className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50" onClick={() => setTreemapPage((p) => Math.min(totalPages, p + 1))} disabled={treemapPage >= totalPages}>Siguiente ▶</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="h-[600px] md:h-[650px] xl:h-[700px] 2xl:h-[760px] relative flex flex-col gap-3">
                <div className="flex flex-wrap gap-3 text-xs">
                  {[...new Set(treemap.map(r=>r.defect_type))].map(dt => (
                    <div key={dt} className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{backgroundColor: colorForType(dt)}}></span>
                      <span className="text-gray-700">{dt}</span>
                    </div>
                  ))}
                </div>
                <div className="flex-1">
                  <ChartComponent
                    ref={treemapChartRef}
                    type="treemap"
                    data={treemapChartData}
                    options={treemapOptions}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
